import React, { useState, useEffect } from 'react';
import { Typography } from '@/UIlibrary/Typography/Typography';
import { useTranslations } from 'use-intl';
import { toTranslationKey, tForComponent } from '@/utils/translations';
import QuestionnaireInput from './QuestionnaireInput';
import { formatValue, getSelectOptions } from './utils';
import EmptySection from './EmptySection';
import { useUpdateQuestionnaireStore } from '@/stores/useUpdateQuestionnaire';
import { CreditDetails as CreditDetailsType, Maybe } from '@/graphql/__generated__/credit-requests';
import QuestionnaireEditableInput from './QuestionnaireEditableInput';

interface CreditDetailsProps {
	data: Maybe<CreditDetailsType> | undefined;
	title: string;
	isEditing?: boolean;
	inputErrors?: Record<string, string>;
}

enum inputErrorMapping {
	creditUse = 'credit_use',
	creditUseDetail = 'credit_use_detail',
	amountRequested = 'amount_requested',
	expectedAdditionalIncome = 'expected_additional_income'
}

const CreditDetails: React.FC<CreditDetailsProps> = ({ data, title, isEditing, inputErrors }) => {
	const t = useTranslations('MicrocreditRequestQuestionnaire');
	const { creditDetails, updateCreditDetails } = useUpdateQuestionnaireStore();
	const [formData, setFormData] = useState(creditDetails as CreditDetailsType);

	useEffect(() => {
		setFormData(creditDetails);
	}, [creditDetails]);

	const handleChange = (key: string, value: any) => {
		setFormData((prevData: any) => ({
			...prevData,
			[key]: value
		}));
		updateCreditDetails({ [key]: value });
	};
	const editableFields = () => {
		return (
			<>
				<div className="w-1/2 px-3 py-1">
					<QuestionnaireEditableInput
						title={'creditUse'}
						value={formData['creditUse'] || data?.creditUse || ''}
						options={getSelectOptions('creditUse', t)}
						isSelect={true}
						onChange={(newValue) => handleChange('creditUse', newValue)}
						error={inputErrors?.[inputErrorMapping.creditUse] || ''}
					/>
				</div>
				<div className="w-1/2 px-3 py-1">
					<QuestionnaireEditableInput
						title={'creditUseDetail'}
						value={
							formData['creditUseDetail'] !== null
								? formData['creditUseDetail']
								: data?.creditUseDetail || ''
						}
						onChange={(newValue) => handleChange('creditUseDetail', newValue)}
						error={inputErrors?.[inputErrorMapping.creditUseDetail] || ''}
					/>
				</div>
				<div className="w-1/2 px-3 py-1">
					<QuestionnaireEditableInput
						title={'amountRequested'}
						value={formData['amountRequested'] || data?.amountRequested || ''}
						onChange={(newValue) => handleChange('amountRequested', newValue)}
						error={inputErrors?.[inputErrorMapping.amountRequested] || ''}
						type="money"
					/>
				</div>
				<div className="w-1/2 px-3 py-1">
					<QuestionnaireEditableInput
						title={'expectedAdditionalIncome'}
						value={formData['expectedAdditionalIncome'] || data?.expectedAdditionalIncome || ''}
						onChange={(newValue) => handleChange('expectedAdditionalIncome', newValue)}
						error={inputErrors?.[inputErrorMapping.expectedAdditionalIncome] || ''}
						type="money"
					/>
				</div>
			</>
		);
	};

	return (
		<div className="flex flex-col gap-1 p-4 bg-white rounded-lg w-full">
			<div className={`flex flex-row items-center mb-4`}>
				<div className="grow">
					<Typography weight="semibold">{t(toTranslationKey(title))}</Typography>
				</div>
			</div>
			{data ? (
				<div className="flex flex-row flex-wrap w-full">
					{isEditing ? (
						editableFields()
					) : (
						<>
							<div className="w-1/2 px-3 py-1">
								<QuestionnaireInput
									title={'creditUse'}
									value={formatValue(data.creditUse, t, 'creditUse')}
								/>
							</div>
							<div className="w-1/2 px-3 py-1">
								<QuestionnaireInput
									title={'creditUseDetail'}
									value={formatValue(data.creditUseDetail, t, 'creditUseDetail')}
								/>
							</div>
							<div className="w-1/2 px-3 py-1">
								<QuestionnaireInput
									title={'amountRequested'}
									value={formatValue(data.amountRequested, t, 'amountRequested')}
								/>
							</div>
							<div className="w-1/2 px-3 py-1">
								<QuestionnaireInput
									title={'expectedAdditionalIncome'}
									value={formatValue(data.expectedAdditionalIncome, t, 'expectedAdditionalIncome')}
								/>
							</div>
						</>
					)}
				</div>
			) : (
				<EmptySection t={tForComponent(t)} />
			)}
		</div>
	);
};

export default CreditDetails;
